import React, {
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';

import { Modal } from '@sopranodesign/pulse-components';

import AddSiteModal from './AddSiteModal';
import AddFolderModal from './AddFolderModal';
import RegistrationModal from './RegistrationModal';
import UpdateCredentialModal from './UpdateCredentialModal';
import TagModal from '../TagModal';
import WarningMessageBody from '../WarningMessageBody';
import {
  fetchSharePointTags,
  fetchSharePointAvailableTags
} from '../../api';
import { useTagManager } from '../useTagManager';

const ModalManager = ({ state }) => {
  const {
    showError,
    activeModal,
    showSuccess,
    selectedSite,
    globalLoading,
    refetchFolder,
    deleteResource,
    selectedFolder,
    setActiveModal,
    recrawlResource,
    saveTagsMutation,
    selectedResource,
    createTagMutation,
    deleteSiteMutation,
    disconnectMutation,
    resyncSiteMutation,
    selectedActionSite,
    deleteFolderMutation,
    resyncFolderMutation
  } = state;

  const {
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    handleTagChange
  } = useTagManager({
    scope: 'generative_ai_sharepoint_files',
    createTagFn: createTagMutation.mutateAsync
  });

  const [tagsLoading, setTagsLoading] = useState(false);

  // Fetch tags when tagsModal is active
  useEffect(() => {
    const fetchTagsData = async () => {
      if (activeModal === 'tagsModal' && selectedResource) {
        setTagsLoading(true);
        try {
          const availableTagsResponse = await fetchSharePointAvailableTags();
          setTags(availableTagsResponse?.data?.data ?? []);

          const assignedTagsResponse = await fetchSharePointTags(selectedResource.id);
          const currentTags = assignedTagsResponse?.data?.data ?? [];
          setSelectedTags(currentTags.map(tag => tag.id));
        } catch (err) {
          showError(`Failed to fetch tags: ${err.message}`);
        } finally {
          setTagsLoading(false);
        }
      }
    };
    fetchTagsData();
  }, [setTags, showError, activeModal, setSelectedTags, selectedResource]);

  // Async handlers for resource actions
  const handleDeleteResource = useCallback(async () => {
    try {
      const response = await deleteResource(selectedResource.file_id);
      if (response.status === 202) {
        showSuccess(`Resource "${selectedResource.name}" delete requested`);
      } else {
        showError(`Failed to delete resource: Unexpected status ${response.status}`);
      }
    } catch (err) {
      showError(`Failed to delete resource: ${err.message}`);
    } finally {
      setActiveModal(null);
      refetchFolder();
    }
  }, [showError, showSuccess, setActiveModal, deleteResource, refetchFolder, selectedResource]);

  const handleRecrawlResource = useCallback(async () => {
    try {
      const response = await recrawlResource(selectedResource.file_id);
      if (response.status === 201) {
        showSuccess(`Resource "${selectedResource.name}" recrawl requested`);
      } else {
        showError(`Failed to recrawl resource: Unexpected status ${response.status}`);
      }
    } catch (err) {
      showError(`Failed to recrawl resource: ${err.message}`);
    } finally {
      setActiveModal(null);
      refetchFolder();
    }
  }, [showError, showSuccess, setActiveModal, recrawlResource, refetchFolder, selectedResource]);

  // Memoized modal configs
  const modalConfigs = useMemo(() => ({
    resyncFolder: {
      header: 'Confirm Resync Folder',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to resync the folder "${selectedFolder?.name}"?`}
        contentBody="This will refresh all associated data."
      />,
      confirmText: 'Resync',
      handleConfirm: () => resyncFolderMutation.mutate()
    },
    resyncSite: {
      header: 'Confirm Resync Site',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to resync all folders under the site "${selectedSite?.site_name}"?`}
        contentBody="This will refresh all associated data."
      />,
      confirmText: 'Resync',
      handleConfirm: () => resyncSiteMutation.mutate()
    },
    deleteSite: {
      header: 'Confirm Delete Site',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to delete the site "${selectedActionSite?.site_name}"?`}
        contentBody="All synced folders and files will be permanently removed."
        warningText="This action cannot be undone."
      />,
      confirmText: 'Delete',
      handleConfirm: () => deleteSiteMutation.mutate()
    },
    deleteFolder: {
      header: 'Confirm Delete Folder',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to delete the folder "${selectedFolder?.name}"?`}
        contentBody="All synced files will be permanently removed."
        warningText="This action cannot be undone."
      />,
      confirmText: 'Delete',
      handleConfirm: () => deleteFolderMutation.mutate()
    },
    disconnect: {
      header: 'Confirm Disconnect SharePoint',
      body: <WarningMessageBody
        contentHeader="Are you sure you want to disconnect SharePoint?"
        contentBody="All synced sites, folders, and files will be permanently removed."
        warningText="This action cannot be undone."
      />,
      confirmText: 'Disconnect',
      handleConfirm: () => disconnectMutation.mutate()
    },
    deleteResource: {
      header: 'Confirm Delete Resource',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to delete the resource “${selectedResource?.name}”?`}
        contentBody="All synced data will be permanently removed."
        warningText="This action cannot be undone."
      />,
      confirmText: 'Delete',
      handleConfirm: handleDeleteResource
    },
    recrawlResource: {
      header: 'Confirm Recrawl Resource',
      body: <WarningMessageBody
        contentHeader={`Are you sure you want to recrawl the resource “${selectedResource?.name}”?`}
        contentBody="This will update its content."
      />,
      confirmText: 'Recrawl',
      handleConfirm: handleRecrawlResource
    }
  }), [
    selectedSite,
    selectedFolder,
    selectedResource,
    deleteSiteMutation,
    disconnectMutation,
    resyncSiteMutation,
    selectedActionSite,
    deleteFolderMutation,
    handleDeleteResource,
    resyncFolderMutation,
    handleRecrawlResource
  ]);

  const renderStepModal = () => {
    switch (activeModal) {
      case 'registration': return <RegistrationModal state={state} />;
      case 'addSite': return <AddSiteModal state={state} />;
      case 'addFolder': return <AddFolderModal state={state} />;
      case 'updateCredential': return <UpdateCredentialModal state={state} />;
      default: return null;
    }
  };

  if (activeModal === 'tagsModal' && !tagsLoading) {
    return (
      <TagModal
        isVisible
        header="SharePoint Resource Tags"
        onCancel={() => { setActiveModal(null); }}
        onConfirm={async () => {
          try {
            await saveTagsMutation.mutateAsync({ resourceId: selectedResource.id, groupIds: selectedTags });
            showSuccess('Tags assigned successfully!');
          } catch (err) {
            showError(`Failed to assign tags: ${err.message}`);
          }
        }}
        tags={tags}
        selectedTags={selectedTags}
        handleTagChange={handleTagChange}
      />
    );
  }

  const modal = modalConfigs[activeModal];
  return modal ? (
    <Modal
      body={modal.body}
      cancelText={modal.cancelText || 'Cancel'}
      header={modal.header}
      loading={globalLoading}
      handleCancel={() => { setActiveModal(null); }}
      confirmText={modal.confirmText}
      handleConfirm={modal.handleConfirm}
      disableConfirmButton={globalLoading}
    />
  ) : renderStepModal();
};

ModalManager.propTypes = {
  state: PropTypes.shape({
    tags: PropTypes.array,
    activeModal: PropTypes.string,
    globalLoading: PropTypes.bool,
    selectedTags: PropTypes.array,
    selectedSite: PropTypes.object,
    selectedFolder: PropTypes.object,
    selectedResource: PropTypes.object,
    setTags: PropTypes.func.isRequired,
    selectedActionSite: PropTypes.object,
    showError: PropTypes.func.isRequired,
    showSuccess: PropTypes.func.isRequired,
    queryClient: PropTypes.object.isRequired,
    refetchFolder: PropTypes.func.isRequired,
    deleteResource: PropTypes.func.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    recrawlResource: PropTypes.func.isRequired,
    setSelectedSite: PropTypes.func.isRequired,
    setSelectedTags: PropTypes.func.isRequired,
    setTokenInvalid: PropTypes.func.isRequired,
    setSelectedFolder: PropTypes.func.isRequired,
    saveTagsMutation: PropTypes.object.isRequired,
    createTagMutation: PropTypes.object.isRequired,
    setSelectedResource: PropTypes.func.isRequired,
    deleteSiteMutation: PropTypes.object.isRequired,
    disconnectMutation: PropTypes.object.isRequired,
    resyncSiteMutation: PropTypes.object.isRequired,
    deleteFolderMutation: PropTypes.object.isRequired,
    resyncFolderMutation: PropTypes.object.isRequired
  }).isRequired
};

export default ModalManager;